import { Link } from "react-router-dom";
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CalendarBlank,
  CalendarCheck,
  ChartBar,
  ChartLine,
  ClipboardText,
  Target,
  UsersThree,
  Wallet,
  WarningCircle,
} from "@phosphor-icons/react";
import { IndicativeNote, Ring } from "@/components/frameworkBits";
import { FrameworkSwitcher } from "@/components/FrameworkSwitcher";
import { DINACHARYA_DEMO } from "@/lib/demoScores";
import { useDinacharyaLinks } from "@/lib/dinacharyaLinks";
import { useDinacharyaScores } from "@/lib/dinacharyaScores";

const TEAM_SCORE = DINACHARYA_DEMO.score10;

const BAND_TONE = {
  Excellent: { tone: "text-success", dot: "bg-success" },
  Good: { tone: "text-teal", dot: "bg-teal" },
  Average: { tone: "text-[hsl(28_82%_38%)]", dot: "bg-warning" },
  "Below average": { tone: "text-destructive", dot: "bg-destructive/80" },
  Poor: { tone: "text-destructive", dot: "bg-destructive" },
};
const LEVEL_NAMES = {
  NLT: "Next Level Team", SLT: "Strategic Leadership Team", ELT: "Extended Leadership Team",
};

const BANDS = [
  ["9.0 – 10", "Excellent", "World class performance. Sustainable and scalable.", "bg-success text-white", "text-success"],
  ["7.0 – 9.0", "Good", "Above average. On track for excellence.", "bg-teal text-white", "text-teal"],
  ["5.0 – 7.0", "Average", "Room for improvement. Focus and consistency needed.", "bg-warning text-white", "text-[hsl(28_82%_38%)]"],
  ["3.0 – 5.0", "Below average", "Multiple gaps. Immediate attention required.", "bg-destructive/80 text-white", "text-destructive"],
  ["0.0 – 3.0", "Poor", "Critical gaps. Urgent corrective action needed.", "bg-destructive text-white", "text-destructive"],
];

const FALLBACK_MATRIX = [
  { level: "NLT", name: "Next Level Team", size: 10, score: 8.2, band: "Good", trend: 0.72, tone: "text-teal", dot: "bg-teal" },
  { level: "SLT", name: "Strategic Leadership Team", size: 3, score: 5.3, band: "Average", trend: 0.45, tone: "text-[hsl(28_82%_38%)]", dot: "bg-warning" },
  { level: "ELT", name: "Extended Leadership Team", size: 14, score: 6.8, band: "Average", trend: 0.58, tone: "text-[hsl(28_82%_38%)]", dot: "bg-warning" },
];

const DECK = [
  {
    key: "daily-review-tracker",
    linkKey: "daily_review_tracker",
    icon: CalendarCheck,
    title: "Daily review tracker",
    desc: "Track daily reviews and discipline",
    tone: "bg-[hsl(186_62%_94%)]",
    accent: "text-teal",
  },
  {
    key: "monthly-planner",
    linkKey: "monthly_planner",
    icon: CalendarBlank,
    title: "Monthly planner",
    desc: "Plan budgets and targets for the month",
    tone: "bg-[hsl(212_70%_95%)]",
    accent: "text-deepblue",
    tiles: [
      { label: "Calendar", icon: CalendarBlank, tone: "text-deepblue" },
      { label: "Budget", icon: Wallet, tone: "text-teal" },
      { label: "Target", icon: Target, tone: "text-destructive" },
    ],
  },
  {
    key: "monthly-review-tracker",
    linkKey: "monthly_review_tracker",
    icon: ClipboardText,
    title: "Monthly review tracker",
    desc: "Track monthly performance reviews",
    tone: "bg-[hsl(268_56%_95%)]",
    accent: "text-plumink",
  },
];

export default function Dinacharya() {
  const links = useDinacharyaLinks();
  const { scores, refresh, busy } = useDinacharyaScores();
  const live = (scores?.levels || []).length
    ? scores.levels.map((r) => ({
        level: r.level, name: LEVEL_NAMES[r.level] || r.name, size: r.size,
        score: r.score, band: r.band, trend: r.trend == null ? null : Math.min(Math.max(r.trend > 1 ? r.trend / 10 : r.trend, 0), 1),
        ...(BAND_TONE[r.band] || BAND_TONE.Average),
      }))
    : null;
  const matrix = live || FALLBACK_MATRIX;
  const teamScore = scores?.team_score ?? TEAM_SCORE;
  const teamBand = scores?.team_band ?? DINACHARYA_DEMO.band;
  const updated = scores?.fetched_at
    ? new Date(scores.fetched_at).toLocaleString(undefined,
        { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <div className="space-y-10" data-testid="dinacharya-overview">
      <FrameworkSwitcher current="dinacharya" />
      <div className="panel overflow-hidden">
        <div className="brand-bar h-1.5 w-full" />
        <div className="flex flex-col gap-8 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="flex items-start gap-5">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-navy text-white">
              <CalendarCheck size={30} />
            </div>
            <div className="md:flex md:items-start md:gap-8">
              <div className="md:border-r md:border-border md:pr-8">
                <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">Dinacharya</h1>
                <p className="mt-1 text-base font-medium text-deepblue">Team score</p>
              </div>
              <div className="mt-4 max-w-sm md:mt-0">
                <p className="font-display text-xl text-navy">Reduce owner dependence</p>
                <p className="mt-2 text-sm text-foreground/80">
                  Run the business with discipline, review and accountability. From transactions to transformation.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <Ring value={teamScore} tone="stroke-teal" testid="dc-team-score-ring">
              <UsersThree size={26} className="text-navy" />
            </Ring>
            <div>
              <p className="label-caps">Team score</p>
              <p className="font-mono text-5xl font-semibold tabular-nums text-navy" data-testid="dc-team-score">
                {teamScore}
                <span className="text-xl font-semibold text-[hsl(230_28%_30%)]"> /10</span>
              </p>
              <p className="text-sm font-medium text-teal">{teamBand}</p>
              {live && (
                <p className="mt-1 text-[11px] font-medium text-muted-foreground" data-testid="dc-team-score-source">
                  Average of {matrix.map((m) => m.level).join(" · ")} from the score sheet
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {scores?.stale && (
        <div className="flex flex-wrap items-start gap-3 rounded-2xl border-l-4 border-warning bg-warning/10 px-5 py-4"
             data-testid="dc-stale-alert">
          <WarningCircle size={20} className="mt-0.5 shrink-0 text-[hsl(28_82%_38%)]" />
          <div className="min-w-0 text-sm text-[hsl(28_82%_30%)]">
            <p className="font-semibold">
              {scores.error ? "This client's score sheet is not responding" : "These scores look stale"}
            </p>
            <p className="mt-1">
              {scores.error
                || `The NLT / SLT / ELT scores have not changed in ${scores.unchanged_days} days. `
                   + "Check that the score categorisation sheet is being updated."}
            </p>
          </div>
          <button onClick={refresh} disabled={busy} data-testid="dc-stale-retry"
                  className="ml-auto shrink-0 rounded-full border border-[hsl(28_82%_38%)] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[hsl(28_82%_38%)] transition-colors hover:bg-[hsl(28_82%_38%)] hover:text-white disabled:opacity-50">
            Try again
          </button>
        </div>
      )}

      <IndicativeNote testid="dc-indicative-note" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.45fr_1fr]">
        <section className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <ChartBar size={20} className="text-deepblue" />
              <div>
                <h2 className="font-display text-xl text-navy">Score categorisation</h2>
                <p className="card-body">How is our team performing?</p>
              </div>
            </div>
            {scores?.source_configured && (
              <Link to="/dinacharya/score-trends" data-testid="dc-score-trends-link"
                    className="inline-flex items-center gap-1.5 rounded-full border border-plum/35 bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-plum transition-colors hover:bg-plum hover:text-white">
                <ChartLine size={12} /> Score trends
              </Link>
            )}
            {scores?.source_configured && (
              <button onClick={refresh} disabled={busy} data-testid="dc-score-refresh"
                      className="inline-flex items-center gap-1.5 rounded-full border border-teal/40 bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-teal transition-colors hover:bg-teal hover:text-white disabled:opacity-50">
                <ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /> Refresh now
              </button>
            )}
            {links.score_categorisation && (
              <a href={links.score_categorisation} target="_blank" rel="noopener noreferrer"
                 data-testid="dc-score-categorisation-link"
                 className="inline-flex items-center gap-1.5 rounded-full border border-deepblue/30 bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-deepblue transition-colors hover:bg-deepblue hover:text-white">
                Open score categorisation <ArrowSquareOut size={12} />
              </a>
            )}
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="panel overflow-hidden" data-testid="dc-score-bands">
              <p className="border-b border-border px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-navy">
                Score categorisation
              </p>
              <div className="divide-y divide-border">
                {BANDS.map(([range, cat, desc, chip, tone]) => (
                  <div key={cat} className="flex gap-4 px-5 py-3.5" data-testid={`dc-band-${cat.toLowerCase().replace(/ /g, "-")}`}>
                    <span className={`h-fit shrink-0 rounded-full px-2.5 py-1 font-mono text-xs font-semibold tabular-nums ${chip}`}>
                      {range}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${tone}`}>{cat}</p>
                      <p className="mt-1 card-body">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="panel overflow-hidden" data-testid="dc-score-matrix">
              <p className="border-b border-border px-5 py-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-navy">
                SLT · NLT · ELT score matrix
              </p>
              <div className="grid grid-cols-[1.9fr_2.6rem_5.4rem_0.8fr_1.2rem] gap-2 border-b border-border px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                <span>Level</span>
                <span className="text-center">Team</span>
                <span>Score</span>
                <span>vs LM</span>
                <span className="text-center">St.</span>
              </div>
              <div className="divide-y divide-border">
                {matrix.map((m) => (
                  <div
                    key={m.level}
                    className="grid grid-cols-[1.9fr_2.6rem_5.4rem_0.8fr_1.2rem] items-center gap-2 px-4 py-3.5"
                    data-testid={`dc-matrix-row-${m.level}`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-navy text-white">
                        <UsersThree size={13} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-navy">{m.level}</p>
                        <p className="text-[11px] font-medium leading-tight text-[hsl(230_28%_30%)]">{m.name}</p>
                      </div>
                    </div>
                    <span className="text-center font-mono text-sm font-semibold tabular-nums text-navy">{m.size ?? "—"}</span>
                    <div className="min-w-0">
                      <p className={`font-mono text-sm font-semibold tabular-nums ${m.tone}`}>{m.score}</p>
                      <p className={`text-[11px] font-semibold leading-tight ${m.tone}`}>{m.band}</p>
                    </div>
                    <div className="min-w-0" title={m.delta == null ? "Needs a second month of readings" : "Change vs last month"}>
                      {m.delta == null ? (
                        <span className="text-[11px] font-medium text-muted-foreground">—</span>
                      ) : (
                        <span className={`font-mono text-xs font-semibold tabular-nums ${
                          m.delta > 0 ? "text-success" : m.delta < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                          {m.delta > 0 ? "▲" : m.delta < 0 ? "▼" : "→"} {Math.abs(m.delta).toFixed(2)}
                        </span>
                      )}
                    </div>
                    <span className={`h-2.5 w-2.5 justify-self-center rounded-full ${m.dot}`} title={m.band} />
                  </div>
                ))}
              </div>
              <p className="px-4 py-3 text-[11px] font-medium text-muted-foreground" data-testid="dc-matrix-footnote">
                {live
                  ? `Live from the score categorisation sheet${updated ? ` · updated ${updated}` : ""}`
                  : scores?.error
                    ? `Sheet not readable yet — ${scores.error}`
                    : scores && !scores.source_configured
                      ? "Indicative — add this client's score data source in Admin → Dinacharya links to go live."
                      : "Team size, score and 30-day trend are indicative."}
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-5">
          <div className="flex items-center gap-3">
            <CalendarBlank size={20} className="text-teal" />
            <div>
              <h2 className="font-display text-xl text-navy">Review deck</h2>
              <p className="card-body">Review. Analyze. Improve.</p>
            </div>
          </div>

          <div className="space-y-4">
            {DECK.map((d) => {
              const href = links[d.linkKey];
              const Card = href ? "a" : "div";
              return (
              <Card key={d.key}
                    {...(href ? { href, target: "_blank", rel: "noopener noreferrer" } : {})}
                    className={`panel panel-hover block p-5 ${d.tone} ${href ? "cursor-pointer" : ""}`}
                    data-testid={`dc-deck-${d.key}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-card ${d.accent}`}>
                      <d.icon size={20} />
                    </span>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.08em] text-navy">{d.title}</p>
                      <p className="mt-1 card-body">{d.desc}</p>
                    </div>
                  </div>
                  <span
                    data-testid={`dc-deck-goto-${d.key}`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-navy/20 bg-card px-3.5 py-2 text-xs font-bold uppercase tracking-[0.12em] text-navy transition-colors hover:bg-navy hover:text-white"
                  >
                    {href ? "Open" : "Go to"} <ArrowSquareOut size={12} />
                  </span>
                </div>
                {d.tiles && (
                  <div className="mt-5 grid grid-cols-3 gap-3 border-t border-border pt-4">
                    {d.tiles.map((t) => (
                      <div key={t.label} className="flex flex-col items-center gap-1.5" data-testid={`dc-tile-${t.label.toLowerCase()}`}>
                        <t.icon size={20} className={t.tone} />
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">{t.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
